/**
 * Error Boundary Component
 *
 * Catches JavaScript errors in child component tree, logs them to Sentry,
 * and displays a user-friendly fallback UI.
 *
 * @module components/ErrorBoundary
 */

import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui';

interface FallbackProps {
  error: unknown;
  resetError: () => void;
}

/**
 * Get error message from unknown error type
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

/**
 * Get error stack from unknown error type
 */
function getErrorStack(error: unknown): string | undefined {
  if (error instanceof Error) {
    return error.stack;
  }
  return undefined;
}

/**
 * User-friendly error fallback UI
 */
function ErrorFallback({ error, resetError }: FallbackProps) {
  const errorMessage = getErrorMessage(error);
  const errorStack = getErrorStack(error);

  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center p-8">
      <div className="mx-auto max-w-md text-center">
        <div className="mb-4 text-6xl">
          <span role="img" aria-label="Warning">
            &#x26A0;&#xFE0F;
          </span>
        </div>
        <h2 className="mb-2 text-xl font-semibold text-gray-900">Something went wrong</h2>
        <p className="mb-6 text-gray-600">
          We've been notified and are working to fix the issue. Please try again or contact support
          if the problem persists.
        </p>

        {/* Show error details in non-production */}
        {import.meta.env.DEV && (
          <details className="mb-6 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
              Error details (dev only)
            </summary>
            <pre className="mt-2 overflow-auto rounded bg-gray-100 p-3 text-xs text-red-600">
              {errorMessage}
              {errorStack && `\n\n${errorStack}`}
            </pre>
          </details>
        )}

        <div className="flex justify-center gap-3">
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Refresh page
          </Button>
          <Button onClick={resetError}>Try again</Button>
        </div>
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * Error Boundary that integrates with Sentry.
 * Wraps children and catches any errors in the component tree.
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={(props) => (
        <ErrorFallback error={props.error} resetError={props.resetError} />
      )}
      showDialog={false}
      onError={(error, componentStack) => {
        console.error('[ErrorBoundary] Caught error:', error);
        console.error('[ErrorBoundary] Component stack:', componentStack);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

/**
 * Page-level error boundary with specific styling for full-page errors.
 */
export function PageErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={(props) => (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <ErrorFallback error={props.error} resetError={props.resetError} />
        </div>
      )}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
